import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useControls } from 'leva'

import { cartFluxStore, getProductPosition, getFirstProductPosition } from '../../store/uiStore'

// ============================================================
// Flusso "prodotto → cassa": versione LEGGERA.
// Tre fili luminosi che si intrecciano a spirale lungo la
// traiettoria e convergono sulla testa, + un piccolo bagliore
// in testa e un flash all'arrivo. Costi: 3 polilinee (144 punti
// aggiornati a frame), 2 sprite, zero shader custom.
// La versione "cometa" (scia a tubo + particelle) è nella storia
// git al commit 7ac6844, se servisse recuperarla.
// ============================================================

const THREADS = 3
const SEGMENTS = 48       // punti per filo
const TRAIL = 0.3         // frazione di percorso occupata dai fili
const CURVE_FRAMES = 100  // campioni di Frenet per la spirale
// Limiti verticali della traiettoria: il soffitto del negozio è a ~6.7
const QUOTA_MAX = 6.1
const QUOTA_MIN = 0.8

// Texture radiale per il bagliore (generata una volta)
let glowTexture = null
function getGlowTexture() {
  if (glowTexture) return glowTexture
  const c = document.createElement('canvas')
  c.width = c.height = 64
  const ctx = c.getContext('2d')
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.4, 'rgba(255,255,255,0.5)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 64, 64)
  glowTexture = new THREE.CanvasTexture(c)
  return glowTexture
}

// easing: partenza dolce, corsa, arrivo morbido
const ease = (t) => t * t * (3.0 - 2.0 * t)

export default function CartFlux() {
  const config = useControls('Flusso carrello', {
    // Default calibrati a mano col debugContinuo (arrivo = cassa)
    arrivoX: { value: 3.15, min: -11, max: 11, step: 0.01 },
    arrivoY: { value: 2.91, min: 0, max: 7, step: 0.01 },
    arrivoZ: { value: -4.1, min: -11, max: 11, step: 0.01 },
    altezzaArco: { value: 1.2, min: 0, max: 4, step: 0.1 },
    partenzaOffsetY: { value: 0.3, min: 0, max: 2, step: 0.05 },
    durata: { value: 2.0, min: 0.4, max: 4, step: 0.1 },
    // Quanto la traiettoria devia dal semplice arco (0 = arco pulito)
    casualita: { value: 1.4, min: 0, max: 4, step: 0.1 },
    spirale: { value: 0.16, min: 0, max: 0.6, step: 0.01 },
    colore: '#8fd8ff',
    dimensione: { value: 0.07, min: 0.01, max: 0.4, step: 0.01 },
    debugContinuo: false,
  }, { collapsed: true })

  const [fluxes, setFluxes] = useState([])

  useEffect(() => {
    return cartFluxStore.subscribe(() => {
      const event = cartFluxStore.get()
      if (!event) return
      const from = getProductPosition(event.productId)
      if (!from) return
      setFluxes((fs) => [...fs, { key: event.id, from }])
    })
  }, [])

  const debugFrom = config.debugContinuo
    ? getFirstProductPosition() || [0, 3.8, -2]
    : null

  return (
    <>
      {config.debugContinuo && (
        <>
          <FluxThreads key="debug" from={debugFrom} config={config} loop />
          <mesh position={[config.arrivoX, config.arrivoY, config.arrivoZ]}>
            <sphereGeometry args={[0.12, 16, 16]} />
            <meshBasicMaterial color={config.colore} transparent opacity={0.85} depthWrite={false} />
          </mesh>
        </>
      )}
      {fluxes.map((f) => (
        <FluxThreads
          key={f.key}
          from={f.from}
          config={config}
          onDone={() => setFluxes((fs) => fs.filter((x) => x.key !== f.key))}
        />
      ))}
    </>
  )
}

function FluxThreads({ from, config, onDone, loop = false }) {
  const startTime = useRef(null)
  const done = useRef(false)
  const prevRaw = useRef(0)
  const [seed, setSeed] = useState(0)

  const headRef = useRef()
  const flashRef = useRef()

  // --- Traiettoria randomizzata + frame di Frenet per la spirale ---
  // Serpentina: le deviazioni laterali ALTERNANO il segno (vere curve a S,
  // non zigzag casuale) con ampiezza random; in verticale piccole
  // ondulazioni su/giù sopra un arco contenuto, con quota SEMPRE
  // tra pavimento e soffitto. Anche i giri della treccia sono casuali.
  const { curve, frames, turns } = useMemo(() => {
    const start = new THREE.Vector3(from[0], from[1] + config.partenzaOffsetY, from[2])
    const end = new THREE.Vector3(config.arrivoX, config.arrivoY, config.arrivoZ)
    const dir = end.clone().sub(start)
    const side = new THREE.Vector3(-dir.z, 0, dir.x).normalize()

    const points = [start]
    const TAPPE = 4
    let segno = Math.random() < 0.5 ? 1 : -1
    for (let i = 1; i <= TAPPE; i++) {
      const t = i / (TAPPE + 1)
      const p = start.clone().lerp(end, t)
      // arco verticale contenuto + ondulazione casuale su/giù
      const arco = Math.sin(Math.PI * t) * config.altezzaArco * (0.4 + Math.random() * 0.6)
      const onda = (Math.random() - 0.5) * 0.9
      p.y = THREE.MathUtils.clamp(p.y + arco + onda, QUOTA_MIN, QUOTA_MAX)
      // serpentina laterale: segno alternato, ampiezza casuale
      segno = -segno
      p.add(side.clone().multiplyScalar(
        segno * (0.4 + Math.random() * 0.6) * config.casualita * Math.sin(Math.PI * t)
      ))
      points.push(p)
    }
    points.push(end)
    const curve = new THREE.CatmullRomCurve3(points)
    const frames = curve.computeFrenetFrames(CURVE_FRAMES, false)
    const turns = 4 + Math.random() * 4 // giri della treccia: 4..8
    return { curve, frames, turns }
    // eslint-disable-next-line
  }, [from, seed, config.arrivoX, config.arrivoY, config.arrivoZ, config.altezzaArco, config.partenzaOffsetY, config.casualita])

  // --- I tre fili: Line con gradiente di colore fisso (coda scura →
  // testa chiara: con blending additivo equivale a una dissolvenza) ---
  const threads = useMemo(() => {
    const color = new THREE.Color(config.colore)
    return Array.from({ length: THREADS }, (_, k) => {
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(SEGMENTS * 3), 3))
      const colors = new Float32Array(SEGMENTS * 3)
      for (let j = 0; j < SEGMENTS; j++) {
        const frac = j / (SEGMENTS - 1) // 0 = coda, 1 = testa
        const c = color.clone().lerp(new THREE.Color('white'), frac * 0.5).multiplyScalar(0.05 + 0.95 * frac * frac)
        colors[j * 3] = c.r
        colors[j * 3 + 1] = c.g
        colors[j * 3 + 2] = c.b
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
      const line = new THREE.Line(geometry, material)
      line.frustumCulled = false
      return { line, phase: (k / THREADS) * Math.PI * 2 }
    })
    // eslint-disable-next-line
  }, [])
  useEffect(() => () => threads.forEach(({ line }) => {
    line.geometry.dispose()
    line.material.dispose()
  }), [threads])
  useEffect(() => {
    // aggiorna il gradiente se cambia il colore dagli slider
    const color = new THREE.Color(config.colore)
    threads.forEach(({ line }) => {
      const colors = line.geometry.attributes.color
      for (let j = 0; j < SEGMENTS; j++) {
        const frac = j / (SEGMENTS - 1)
        const c = color.clone().lerp(new THREE.Color('white'), frac * 0.5).multiplyScalar(0.05 + 0.95 * frac * frac)
        colors.setXYZ(j, c.r, c.g, c.b)
      }
      colors.needsUpdate = true
    })
  }, [config.colore, threads])

  const tmp = useRef({ p: new THREE.Vector3(), off: new THREE.Vector3() }).current

  useFrame((state) => {
    const now = state.clock.elapsedTime
    if (startTime.current === null) startTime.current = now
    const elapsed = now - startTime.current

    let raw = elapsed / config.durata
    if (loop) {
      raw = raw % (1 + TRAIL)
      if (raw < prevRaw.current) setSeed((s) => s + 1)
      prevRaw.current = raw
    }

    // testa con easing; oltre l'arrivo prosegue lineare (i fili rientrano)
    const head = ease(Math.min(raw, 1)) + Math.max(0, raw - 1)
    const headT = Math.min(ease(Math.min(raw, 1)), 1)
    const fade = THREE.MathUtils.clamp((head - 1) / TRAIL, 0, 1)

    // ---- fili intrecciati ----
    for (let k = 0; k < THREADS; k++) {
      const { line, phase } = threads[k]
      const positions = line.geometry.attributes.position
      for (let j = 0; j < SEGMENTS; j++) {
        const frac = j / (SEGMENTS - 1) // 0 = coda, 1 = testa
        const t = THREE.MathUtils.clamp(head - (1 - frac) * TRAIL, 0, 1)
        curve.getPoint(t, tmp.p)
        // spirale: raggio massimo in coda, zero in testa (i fili si uniscono);
        // sin(π·t) li tiene stretti anche a partenza e arrivo
        const r = config.spirale * (1 - frac) * (0.25 + 0.75 * Math.sin(Math.PI * t))
        const angle = phase + t * turns * Math.PI * 2
        const fi = Math.min(Math.floor(t * CURVE_FRAMES), CURVE_FRAMES - 1)
        tmp.off
          .copy(frames.normals[fi]).multiplyScalar(Math.cos(angle) * r)
          .addScaledVector(frames.binormals[fi], Math.sin(angle) * r)
        tmp.p.add(tmp.off)
        positions.setXYZ(j, tmp.p.x, tmp.p.y, tmp.p.z)
      }
      positions.needsUpdate = true
      line.material.opacity = 1 - fade
    }

    // ---- bagliore in testa ----
    if (headRef.current) {
      curve.getPoint(headT, tmp.p)
      headRef.current.position.copy(tmp.p)
      const pulse = 1 + Math.sin(now * 18) * 0.15
      headRef.current.scale.setScalar(config.dimensione * 4 * pulse)
      headRef.current.material.opacity = raw >= 1 ? Math.max(0, 1 - (raw - 1) / 0.15) : 0.9
    }

    // ---- flash all'arrivo ----
    if (flashRef.current) {
      const it = THREE.MathUtils.clamp((raw - 1) / TRAIL, 0, 1)
      curve.getPoint(1, tmp.p)
      flashRef.current.position.copy(tmp.p)
      flashRef.current.scale.setScalar(0.2 + it * 1.6)
      flashRef.current.material.opacity = raw >= 1 ? 0.9 * (1 - it) : 0
    }

    if (!loop && raw > 1 + TRAIL && !done.current) {
      done.current = true
      onDone()
    }
  })

  return (
    <>
      {threads.map(({ line }, k) => (
        <primitive key={k} object={line} />
      ))}
      <sprite ref={headRef} frustumCulled={false}>
        <spriteMaterial map={getGlowTexture()} color={config.colore} transparent depthWrite={false} blending={THREE.AdditiveBlending} />
      </sprite>
      <sprite ref={flashRef} frustumCulled={false}>
        <spriteMaterial map={getGlowTexture()} color="white" transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} />
      </sprite>
    </>
  )
}
